import type { Condition, ExpressionBuilder, Transaction } from "@rocicorp/zero";
import type { Context as ZeroContext, Schema } from "../../zero/schema";
import type { z } from "zod/v4";
import type { ClientHook, PostCommitHook, PreCommitHook, ServerHook } from "../common";
import type { InferToolInput, InferToolOutput, ModelMessage, StreamTextResult, ToolSet } from "ai";
import type { UserActor } from "../../actor";
import type { ProviderRegistry } from "../provider-registry";
import type { AgentSession } from "../agent-session";

/**
 * Agent-definition contract types for client/server agent surfaces.
 *
 * Invariants:
 * - Every agent exposes create/edit/view permission hooks.
 * - Server chat handlers and tool hooks preserve pre-commit/post-commit phases.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
export type BaseAgent<
  AgentType extends string,
  Version extends number,
  Params,
  Context = undefined,
> = {
  agentType: AgentType;
  version: Version;
  canCreate: (opts: {
    tx: Transaction;
    ctx: ZeroContext;
    args: { params: Params };
  }) => Promise<boolean>;
  canEdit: (opts: {
    tx: Transaction;
    ctx: ZeroContext;
    args: { sessionId: string };
  }) => Promise<boolean>;
  viewable: (eb: ExpressionBuilder<"agentSession", Schema>, actor: UserActor) => Condition;
  paramsSchema?: z.ZodType<Params, Params>;
  context?: ContextConfig<Params, Context>;
};

export type ContextConfig<
  Params,
  Context,
  Schema extends z.ZodType<Context> = z.ZodType<Context>,
> = {
  schema: Schema;
  resolve: (opts: {
    tx: Transaction;
    ctx: ZeroContext;
    args: { params: Params };
  }) => Promise<Context>;
};

/**
 * Client-side chat configuration for an agent.
 * Defines the tools available during agent-level chat.
 */
export type ClientChatConfig<Tools extends ToolSet = ToolSet, Context = undefined> = {
  tools?: Tools;
  resolveTools?: (opts: { context: Context }) => ToolSet;
};

/**
 * Server-side chat handler that executes agent-level chat.
 * Called when a user sends a message to the agent (not within a workflow step).
 */
export type ServerChatHandler<_Tools extends ToolSet = ToolSet, Context = undefined> = {
  run: (args: {
    sessionId: string;
    context: Context;
    zeroContext: ZeroContext;
    providerRegistry: ProviderRegistry;
    messages: ModelMessage[];
    abortSignal?: AbortSignal;
  }) => Promise<StreamTextResult<any, any>>;
};

/**
 * Tool data for agent-level chat tool output callback.
 */
export type ChatToolData<Tools extends ToolSet> = {
  [ToolName in keyof Tools]: {
    name: ToolName;
    input: InferToolInput<Tools[ToolName]>;
    output: InferToolOutput<Tools[ToolName]>;
  };
}[keyof Tools];

/**
 * Pre-commit callback for tool output processing.
 * Runs inside the transaction that persists the tool output.
 * Use this to create workflow records atomically with tool output.
 *
 * @returns A result object that will be passed to the post-commit callback.
 */
export type OnChatToolOutputPreCommitCallback<
  Tools extends ToolSet,
  PreCommitHookContext,
  PreCommitResult,
> = (opts: {
  tx: Transaction;
  ctx: PreCommitHookContext;
  sessionId: string;
  tool: ChatToolData<Tools>;
}) => Promise<PreCommitResult | void>;

/**
 * Post-commit callback for tool output processing.
 * Runs after the transaction that persisted the tool output is committed.
 * Use this to trigger external side effects like Durable Object calls.
 *
 * @param preCommitResult - The result from the pre-commit callback, if any.
 */
export type OnChatToolOutputPostCommitCallback<
  Tools extends ToolSet,
  PostCommitHookContext,
  PreCommitResult,
> = (opts: {
  ctx: PostCommitHookContext;
  sessionId: string;
  tool: ChatToolData<Tools>;
  preCommitResult?: PreCommitResult;
}) => Promise<void>;

/**
 * Tool output hook with pre-commit and post-commit phases.
 * Pre-commit runs inside the transaction for atomic database operations.
 * Post-commit runs after commit for external side effects.
 */
export type OnChatToolOutputHook<
  Tools extends ToolSet,
  PreCommitHookContext,
  PostCommitHookContext,
  PreCommitResult = unknown,
> = {
  preCommit?: OnChatToolOutputPreCommitCallback<Tools, PreCommitHookContext, PreCommitResult>;
  postCommit?: OnChatToolOutputPostCommitCallback<Tools, PostCommitHookContext, PreCommitResult>;
};

/**
 * Server-side chat configuration extends client config with execution handler
 * and optional tool output hook.
 */
export type ServerChatConfig<
  Tools extends ToolSet = ToolSet,
  Context = undefined,
  PreCommitHookContext = unknown,
  PostCommitHookContext = unknown,
  ChatToolOutputPreCommitResult = unknown,
> = ClientChatConfig<Tools, Context> & {
  handler?: ServerChatHandler<Tools, Context>;
  onToolOutput?: OnChatToolOutputHook<
    Tools,
    PreCommitHookContext,
    PostCommitHookContext,
    ChatToolOutputPreCommitResult
  >;
};

export type ClientAgent<
  AgentType extends string = string,
  Version extends number = number,
  Params = undefined,
  Context = undefined,
  OnCreatePayload = undefined,
  ChatTools extends ToolSet = ToolSet,
> = ClientAgentSurface<AgentType, Version, Params, Context, OnCreatePayload, ChatTools> & {
  location: "client";
  hooks: {
    onCreate: ClientHook<{ sessionId: string; params: Params; context: Context }, OnCreatePayload>;
  };
  chat?: ClientChatConfig<ChatTools, Context>;

  onCreate: <P>(
    hook: PreCommitHook<{ sessionId: string; params: Params; context: Context }, P>,
  ) => ClientAgent<AgentType, Version, Params, Context, P, ChatTools>;
};

export type ClientAgentSurface<
  AgentType extends string = string,
  Version extends number = number,
  Params = undefined,
  Context = undefined,
  OnCreatePayload = undefined,
  ChatTools extends ToolSet = ToolSet,
> = BaseAgent<AgentType, Version, Params, Context> & {
  /**
   * Non-discriminating location field to keep server agents assignable to client surface.
   */
  location: "client" | "server";
  hooks: {
    onCreate: ClientHook<{ sessionId: string; params: Params; context: Context }, OnCreatePayload>;
  };
  chat?: ClientChatConfig<ChatTools, Context>;
};

export type AnyClientAgent = ClientAgentSurface<any, any, any, any, any, any>;

export type ServerAgent<
  AgentType extends string = string,
  Version extends number = number,
  Params = undefined,
  Context = undefined,
  OnCreatePayload = undefined,
  PreCommitHookContext = unknown,
  PostCommitHookContext = undefined,
  ChatTools extends ToolSet = ToolSet,
  ChatToolOutputPreCommitResult = unknown,
> = ClientAgentSurface<AgentType, Version, Params, Context, OnCreatePayload, ChatTools> & {
  location: "server";
  chat?: ServerChatConfig<
    ChatTools,
    Context,
    PreCommitHookContext,
    PostCommitHookContext,
    ChatToolOutputPreCommitResult
  >;

  hooks: ServerAgentHooks<Params, Context, OnCreatePayload, PostCommitHookContext>;

  systemPrompt: (opts: { context: Context }) => string;

  onCreate: (
    hook: PostCommitHook<
      { sessionId: string; params: Params; context: Context },
      OnCreatePayload,
      PostCommitHookContext
    >,
  ) => ServerAgent<
    AgentType,
    Version,
    Params,
    Context,
    OnCreatePayload,
    PreCommitHookContext,
    PostCommitHookContext,
    ChatTools,
    ChatToolOutputPreCommitResult
  >;

  saveAgentMessage: (opts: SaveAgentMessageOptions) => Promise<void>;
};

export type SaveAgentMessageOptions = {
  sessionId: string;
  messageId: string;
  parts: AgentSession.MessageParts;
};

export type AnyServerAgent = ServerAgent<any, any, any, any, any, any, any, any, any>;

export type ServerAgentHooks<Params, Context, OnCreatePayload, PostCommitHookContext> = {
  onCreate: ServerHook<
    { sessionId: string; params: Params; context: Context },
    OnCreatePayload,
    PostCommitHookContext
  >;
};

export type ServerAgentWithPostCommitHookContext<PostCommitHookContext> = ServerAgent<
  any,
  any,
  any,
  any,
  any,
  any,
  PostCommitHookContext,
  any,
  any
>;

type DefineAgentArgs<
  AgentType extends string,
  Version extends number,
  Params,
  Context,
  ChatTools extends ToolSet = ToolSet,
> = Omit<
  ClientAgent<AgentType, Version, Params, Context, any, ChatTools>,
  "hooks" | "onCreate" | "location"
>;

/**
 * Infer chat tools type from a client agent definition.
 */
export type InferAgentChatTools<Agent extends AnyClientAgent> =
  Agent extends ClientAgent<any, any, any, any, any, infer ChatTools> ? ChatTools : never;

/**
 * Infer context type from an agent definition.
 */
export type InferAgentContext<Agent extends AnyClientAgent | AnyServerAgent> =
  Agent extends BaseAgent<any, any, any, infer Context> ? Context : never;

/**
 * Infer params type from an agent definition.
 */
export type InferAgentParams<Agent extends AnyClientAgent | AnyServerAgent> =
  Agent extends BaseAgent<any, any, infer Params, any> ? Params : never;

export function defineAgent<
  AgentType extends string,
  Version extends number,
  Params = undefined,
  Context = undefined,
  ChatTools extends ToolSet = ToolSet,
>(args: DefineAgentArgs<AgentType, Version, Params, Context, ChatTools>) {
  return newClientAgent(args) as unknown as ClientAgent<
    AgentType,
    Version,
    Params,
    Context,
    undefined,
    ChatTools
  >;
}

const newClientAgent = (
  args: DefineAgentArgs<any, any, any, any, any> | ClientAgent<any, any, any, any, any, any>,
): ClientAgent<any, any, any, any, any, any> => {
  let base: Omit<ClientAgent<any, any, any, any, any, any>, "onCreate">;
  if ("hooks" in args) {
    base = args;
  } else {
    base = {
      ...args,
      location: "client",
      hooks: { onCreate: { preCommit: undefined } },
    };
  }

  return {
    ...base,
    onCreate: (hook) =>
      newClientAgent({
        ...base,
        hooks: { ...base.hooks, onCreate: { preCommit: hook } },
      }),
  };
};
